<template lang="pug">
layout-main(:showSidebar="false" :hideHeader="hideHeader")
  managementPageWithActions.scrollable(:title="$t('customer_tab.title')"
                classPrefix="customer-info"
                :class="{ 'bulk-actions-layout': bulkAsyncActions }"
                @fetchData="loadList"
                @chooseAction="chooseAction"
                @downloadData="downloadData"
                @stopDataLoading="stopLoadAll",
                @toggleOpen="toggleOpen"
                @selectVisibleRows="selectVisibleRows"
                @unselectAll="bulkAsyncActions ? clearCustomerSelections() : clearCustomerTableSelection()"
                @selectAllResults="selectAllResults"
                :bulkAsyncActions="bulkAsyncActions"
                :allResultsSelected="allResultsSelected"
                :currentPageSelected="currentPageSelected"
                :searchable="false"
                :isDataLoading="isLoadingAll || isLoading",
                :chosenAction="chosenAction"
                :loadWithQuery="$route.query.query"
                :searchPageSize="searchPageSize"
                :suppressFetchOnScroll="true"
                :showDatePicker="false"
                :disabledDatePickerRange="disabledDatePickerRange"
                :customerAgentFilter="false"
                :customerChannelFilter="false"
                :customerSegmentFilter="false"
                :customerProfileFilter="!!profileSearchableFields.length"
                :customerProfileOptions="profileSearchableFields"
                :actionOptions="aclPermittedActions"
                :showTotals="false"
                :showLoadAll="false"
                :filteredSearch="false"
                :showDownloadButton="false"
                :pagination="pagination"
                :creatable="false"
                :helpText="helpText"
                :actionsErrorText="actionsErrorText"
                :hasRowSelections="hasRowSelections"
                :openFilters="isFilterOpen"
                :searchOnEnterOnly="true"
                :hasSelectAndLoad="true"
                :selectedRowsCount="selectedRowsCount"
                ref="managementPage")

    template(v-slot:editor)
      actions-panel(v-if="actionFormShown"
                  v-bind="{...actionCardParams}"
                  @commitAction="commitAction"
                  @closeActionPanel="closeActionPanel")

    template(v-slot:controls)
      customer-table-controls(:customerProfileOptions="profileSearchableFields"
                            :customerAgentOptions="Object.keys(advancedCustomerAgentOptions)"
                            :customerChannelOptions="sortedChannels"
                            :customerSegmentOptions="segments"
                            :customerTagOptions="tagsListState"
                            :teamFilterOptions="teams"
                            :agentFilterOptions="sortedAgents"
                            :disabledDatePickerRange="disabledDatePickerRange"
                            :pagination="pagination"
                            :showExport="showCustomersExport"
                            :isExporting="isExporting"
                            :exportDisabled="isLoading"
                            @export="exportCustomers"
                            @apply-filter="applyAdvancedFilter")


    template(v-slot:body v-if="isMobileContext")
      aiq-table.customers-scrollbar(:data="customers"
                        style="width: 100%"
                        ref="customersTable"
                        row-key="id"
                        :default-sort="defaultSortOrder"
                        v-loading="actionInProgress"
                        @selection-change="onSelect"
                        @select-all="onSelect")
        aiq-table-column(type="selection",
                        width="48px",
                        :reserve-selection="true"
                        :selectable="isCustomerSelectable"
                        label-class-name="is-bordered")
        aiq-table-column(type="expand")
          template(v-slot:default="item")
            .expandable-row
              p(v-if="getIsCustomerTableColumnVisible('email')")
                span.col-label {{ getCustomerTableColumnLabel('email') }}:
                span.col-value {{ getCustomerEmail(item.row) }} &nbsp;
              p(v-if="getIsCustomerTableColumnVisible('joined_date')")
                span.col-label {{ getCustomerTableColumnLabel('joined_date') }}:
                span.col-value {{ getFormattedJoinedDate(item.row) }} &nbsp;
              p(v-if="getIsCustomerTableColumnVisible('segment')")
                span.col-label {{ getCustomerTableColumnLabel('segment') }}:
                span.col-value {{ getFormattedCustomerSegment(item.row) }} &nbsp;
              p(v-if="getIsCustomerTableColumnVisible('primary_agent')")
                span.col-label {{ getCustomerTableColumnLabel('primary_agent') }}:
                aiq-button(link type="primary" size="small", @click="canView('/users') && filterToAgent(item.row.primary_agent_details)") {{ getPrimaryAgentName(item.row) }}
              p(v-if="getIsCustomerTableColumnVisible('assigned_agents')")
                span.col-label {{ getCustomerTableColumnLabel('assigned_agents') }}:
                .pills-list
                  aiq-button(v-for="agent in item.row.assigned_agents_details"
                          text
                          size="small",
                          class="agent-button"
                          :key="agent.id"
                          @click="canView('/users') && filterToAgent(agent)")
                    .tag-tooltip(v-if="getAssignedAgentName(agent).length >= maxAssignedAgentNameLength")
                      aiq-tooltip(:content="getAssignedAgentName(agent)")
                        aiq-tag {{ getTruncatedAssignedAgentName(agent) }}
                    aiq-tag(v-else) {{ getAssignedAgentName(agent) }}
              p(v-if="getIsCustomerTableColumnVisible('last_feedback_sent')")
                span.col-label {{ getCustomerTableColumnLabel('last_feedback_sent') }}:
                span.col-value {{ getFormattedFeedbackTime(item.row, 'customer_feedback_details.last_feedback_sent_at') }} &nbsp;
              p(v-if="getIsCustomerTableColumnVisible('last_feedback_completed')")
                span.col-label {{ getCustomerTableColumnLabel('last_feedback_completed') }}:
                span.col-value {{ getFormattedFeedbackTime(item.row, 'customer_feedback_details.last_feedback_completed_at') }} &nbsp;

        aiq-table-column(:label="getCustomerTableColumnLabel('name')", :sortable="true", :sort-method="onSortName", label-class-name="is-bordered")
          template(v-slot:default="item")
            span {{ $filters.toUpperFirst(getCustomerName(item.row)) }} &nbsp;
        aiq-table-column(v-if="getIsCustomerTableColumnVisible('last_interaction')"
                         prop="last_chat_details.last_chat_time"
                         :label="getCustomerTableColumnLabel('last_interaction')"
                         :sortable="true"
                         :sort-method="onSortByTime('last_chat_details.last_chat_time')"
                         label-class-name="is-bordered")
          template(v-slot:default="item")
            aiq-button.date-button(link type="primary" size="small", @click="goToLastConversation(item.row)") {{ getFormattedLastChatTime(item.row) }}

    template(v-slot:body v-if="!isMobileContext")
      aiq-table.customers-scrollbar(:data="customers"
                      ref="customersTable"
                      row-key="id"
                      :default-sort="defaultSortOrder"
                      v-loading="actionInProgress"
                      @selection-change="onSelect"
                      @select-all="onSelect")
        aiq-table-column(type="selection",
                        width="48px",
                        :reserve-selection="true"
                        :selectable="isCustomerSelectable"
                        label-class-name="is-bordered")
        template(v-for="column in visibleCustomerTableColumns" :key="column.name")
          aiq-table-column(v-if="column.name === 'name'"
                           :label="getCustomerTableColumnLabel('name')"
                           :sortable="true"
                           :sort-method="onSortName"
                           label-class-name="is-bordered")
            template(v-slot:default="scope")
              tooltip-info(:item="scope.row")
                template(v-slot:customerInfoItem) {{ $filters.toUpperFirst(getCustomerName(scope.row)) }} &nbsp;
          aiq-table-column(v-else-if="column.name === 'email'"
                           :label="getCustomerTableColumnLabel('email')"
                           :sortable="true"
                           :sort-method="onSortEmail"
                           label-class-name="is-bordered")
            template(v-slot:default="scope")
              span {{ getCustomerEmail(scope.row) }} &nbsp;
          aiq-table-column(v-else-if="column.name === 'phone'"
                           :label="getCustomerTableColumnLabel('phone')"
                           :sortable="true"
                           :sort-method="onSortPhone"
                           label-class-name="is-bordered")
            template(v-slot:default="scope")
              span {{ getCustomerPhone(scope.row) }} &nbsp;
          aiq-table-column(v-else-if="column.name === 'external_id'"
                           :label="getCustomerTableColumnLabel('external_id')"
                           :sortable="true"
                           :sort-method="onSortExternalId"
                           label-class-name="is-bordered")
            template(v-slot:default="scope")
              span {{ getCustomerExternalId(scope.row) }} &nbsp;
          aiq-table-column(v-else-if="column.name === 'joined_date'"
                           prop="joined_date"
                           :label="getCustomerTableColumnLabel('joined_date')"
                           :sortable="true"
                           :sort-method="onSortJoinedDate"
                           label-class-name="is-bordered"
                           width="120px")
            template(v-slot:default="scope")
              span {{ getFormattedJoinedDate(scope.row) }} &nbsp;
          aiq-table-column(v-else-if="column.name === 'last_interaction'"
                           prop="last_chat_details.last_chat_time"
                           :label="getCustomerTableColumnLabel('last_interaction')"
                           :sortable="true"
                           :sort-method="onSortByTime('last_chat_details.last_chat_time')"
                           label-class-name="is-bordered")
            template(v-slot:default="scope")
              aiq-button(link type="primary" size="small", @click="goToLastConversation(scope.row)") {{ getFormattedLastChatTime(scope.row) }}
          aiq-table-column(v-else-if="column.name === 'segment'"
                           :label="getCustomerTableColumnLabel('segment')"
                           :sortable="true"
                           :sort-method="onSortCustomerSegment"
                           label-class-name="is-bordered")
            template(v-slot:default="scope")
              span {{ getFormattedCustomerSegment(scope.row) }} &nbsp;
          aiq-table-column(v-else-if="column.name === 'primary_agent'"
                           :label="getCustomerTableColumnLabel('primary_agent')"
                           :sortable="true"
                           :sort-method="onSortPrimaryAgent"
                           label-class-name="is-bordered")
            template(v-slot:default="scope")
              aiq-button(link type="primary" size="small", class="agent-button", @click="canView('/users') && filterToAgent(scope.row.primary_agent_details)") {{ getPrimaryAgentName(scope.row) }}
          aiq-table-column(v-else-if="column.name === 'assigned_agents'"
                           :label="getCustomerTableColumnLabel('assigned_agents')"
                           :sortable="false"
                           width="170px"
                           label-class-name="is-bordered")
            template(v-slot:default="scope")
              .tag-list
                aiq-button(v-for="agent in scope.row.assigned_agents_details"
                         text
                         size="small",
                         class="agent-button"
                         :key="agent.id"
                         @click="canView('/users') && filterToAgent(agent)")
                  .tag-tooltip(v-if="getAssignedAgentName(agent).length >= maxAssignedAgentNameLength")
                    aiq-tooltip(:content="getAssignedAgentName(agent)")
                      aiq-tag {{ getTruncatedAssignedAgentName(agent) }}
                  aiq-tag(v-else) {{ getAssignedAgentName(agent) }}
          aiq-table-column(v-else-if="column.name === 'last_feedback_sent'"
                           :label="getCustomerTableColumnLabel('last_feedback_sent')"
                           :sortable="true"
                           :sort-method="onSortByTime('customer_feedback_details.last_feedback_sent_at')"
                           label-class-name="is-bordered")
            template(v-slot:default="scope")
              span {{ getFormattedFeedbackTime(scope.row, 'customer_feedback_details.last_feedback_sent_at') }} &nbsp;
          aiq-table-column(v-else-if="column.name === 'last_feedback_completed'"
                           :label="getCustomerTableColumnLabel('last_feedback_completed')"
                           :sortable="true"
                           :sort-method="onSortByTime('customer_feedback_details.last_feedback_completed_at')"
                           label-class-name="is-bordered")
            template(v-slot:default="scope")
              span {{ getFormattedFeedbackTime(scope.row, 'customer_feedback_details.last_feedback_completed_at') }} &nbsp;
    template(v-slot:bottom-pagination)
      bottom-pagination(v-model:page-size="searchPageSize"
                        @page-size-change="fetchFirstPageAfterPageSizeChange"
                        @update:offset="fetchPageAtOffset"
                        :pagination="pagination"
                        :pageSizeChoices="[25, 50, 100, 250]"
                        :disabled="bulkAsyncActions && isLoading")

</template>

<script>
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import omit from 'lodash/omit';
import isEqual from 'lodash/isEqual';
import merge from 'lodash/merge';
import isEmpty from 'lodash/isEmpty';
import isArray from 'lodash/isArray';
import difference from 'lodash/difference';
import upperFirst from 'lodash/upperFirst';
import dayjs from '@/localPlugins/dayjs';
import { disableFutureDate, alphabetSort } from '@/libs';
import { mapActions, mapGetters, mapState } from 'vuex';
import { PAGINATION_DEFAULT, TAG_LIMIT, TEAM_LIMIT } from '@/constants/pagination';

import ManagementPageWithActions from '@/components/ManagementPage/ManagementPageWithActions.vue';
import LayoutMain from '@/components/layouts/main.vue';
import ActionsPanel from '@/components/ActionsPanel.vue';
import BottomPagination from '@/components/BottomPagination.vue';
import { filterActionsByPermissions } from '../../libs/permissionsHelper';
import { formatCustomerSegmentsForDisplay } from '../../libs/customerSegments';
import { NON_DELETABLE_SEGMENTS } from '@/constants/segment';
import { CHANNELS } from '@/constants';
import { downloadAsFile, downloadDataAsFile } from '@/libs';
import { limitedString } from '@/libs/stringUtils';
import TooltipInfo from './components/TooltipInfo.vue';
import CustomerTableControls from './components/CustomerTableControls.vue';

const SEARCH_PAGE_SIZE = 50;
const CUSTOMERS_EXPORT_WARNING_THRESHOLD = 50;

const SEND_SURVEY_ACTION = 'Request feedback';
const UPDATE_PRIMARY_AGENT = 'Update primary agent';
const UPDATE_CUSTOMER_SEGMENTS = 'Update segments';
const SEND_MESSAGE = 'Send message';
const UPDATE_ASSIGNED_AGENTS = 'Update assigned agents';
const CLOSE_CONVERSATION = 'Close conversation';
const SPECIAl_CHAR = /[!#$%^&*()=[\]{};:"\\|,<>?`]+/;
const MAX_ASSIGNED_AGENT_NAME_LENGTH = 20;
const ACTION_OPTIONS = {
  [UPDATE_PRIMARY_AGENT]: {},
  [SEND_MESSAGE]: {},
  [SEND_SURVEY_ACTION]: {},
  [UPDATE_CUSTOMER_SEGMENTS]: {},
  [UPDATE_ASSIGNED_AGENTS]: {},
  [CLOSE_CONVERSATION]: {},
};

export default {
  name: 'customers',
  components: {
    ManagementPageWithActions,
    LayoutMain,
    ActionsPanel,
    TooltipInfo,
    CustomerTableControls,
    BottomPagination,
  },
  data() {
    return {
      actionInProgress: true,
      maxAssignedAgentNameLength: MAX_ASSIGNED_AGENT_NAME_LENGTH,
      chosenAction: null,
      pagination: PAGINATION_DEFAULT,
      params: {},
      isFilterOpen: false,
      actionFormShown: false,
      pageResource: '/customersdashboard',
      searchPageSize: SEARCH_PAGE_SIZE,
      allResultsSelected: false,
      selectionQuery: null,
      disabledDatePickerRange: disableFutureDate,

      isLoadingAll: false,
      isLoading: false,
      isExporting: false,

      actionOptions: {
        ...ACTION_OPTIONS,
        [UPDATE_PRIMARY_AGENT]: {
          commitActionFunction: this.updatePrimaryAgentFromCustomers,
        },
        [SEND_MESSAGE]: {
          commitActionFunction: this.sendMessageToCustomers,
        },
        [SEND_SURVEY_ACTION]: {
          commitActionFunction: this.sendSurveyToCustomers,
        },
        [UPDATE_CUSTOMER_SEGMENTS]: {
          commitActionFunction: this.updateCustomerSegments,
        },
        [UPDATE_ASSIGNED_AGENTS]: {
          commitActionFunction: this.updateAssignedAgents,
        },
        [CLOSE_CONVERSATION]: {
          commitActionFunction: this.closeConversationToCustomers,
        },
      },
      advancedDashboardAgentFilter: null,
      defaultSortOrder: { prop: 'last_chat_details.last_chat_time', order: 'ascending' },
    };
  },
  props: {
    hideHeader: {
      type: Boolean,
      default: false,
    },
  },
  mounted() {
    this.$store.dispatch('configs/getPostChatSurvey').then(response => {
      this.localPostChatSurvey = response;
    });
    this.$store.dispatch('agents/getAgentsList', [{
      limit: 999999,
    }]);
    this.$store.dispatch('configs/getCustomerSegments');
    this.$store.dispatch('conversations/getChannelsList');
    this.$store.dispatch('tags/getTagsList', [{ limit: TAG_LIMIT }]);
    this.$store.dispatch('teams/getTeamsList', [{ limit: TEAM_LIMIT }]);
  },
  computed: {
    ...mapState({
      bulkAsyncActions: state => state.featureFlags.FEATURE_FLAGS.BULK_ASYNC_ACTIONS,
      permissions: state => state.agent.permissions,
      agents: state => state.agents.agents,
      isMobileContext: state => state.settings.isMobileContext,
      profileFields: state => state.configs.config.side_panel.profile_mapping,
      showSmsChannel: s => s.featureFlags.FEATURE_FLAGS.ALLOW_SMS_CHANNEL_CUSTOMER_TAB_ACTIONS,
      columnsConfig: state => get(state, 'configs.config.customers_table.columns', {}),
    }),
    ...mapGetters({
      customersWithoutFullname: 'customersDashboard/customers',
      selectedCustomers: 'customersDashboard/getSelectedCustomers',
      segments: 'configs/segmentNames',
      tagsListState: 'tags/sortedTagNames',
      watchableTeamIds: 'agent/watchableTeamIds',
      teams: 'teams/sortedTeams',
      sortedChannels: 'conversations/sortedChannels',
      sortedAgents: 'agents/nameSortedAgents',
    }),
    showCustomersExport() {
      return this.canView('/customers/visibility/export_table');
    },
    customers() {
      return this.customersWithoutFullname
        .map(c => {
          c.fullName = c.fullName === ''
            ? this.$t('customer_tab.table.default_customer_name', { customer_id: c.id })
            : c.fullName;
          return c;
        });
    },
    profileSearchableFields() {
      // Searchable fields are those which can be used in customer query filters. They are profile
      // customizable bc no customizable are include by default in customer query filters
      return [...this.profileFields]
        .filter(field => field.type === 'string' && field.customizable)
        .sort(alphabetSort.bind(null, 'label'));
    },
    aclPermittedActions() {
      return filterActionsByPermissions(this.actionOptions, this.permissions);
    },
    actionCardParams() {
      let params = {
        actionValue: this.chosenAction,
        items: this.selectedCustomers,
        selectionScope: this.bulkAsyncActions ? this.selectionScope : '',
        lockItems: this.bulkAsyncActions,
        showMessage: true,
      };

      if (this.chosenAction === SEND_SURVEY_ACTION) {
        const optionsValues = (this.localPostChatSurvey && this.localPostChatSurvey.survey_type) ?
          [this.localPostChatSurvey.survey_type.toUpperCase()] : ['CSAT', 'NPS'];
        params = merge(params, {
          showOptions: true,
          showMessage: false,
          requireOption: true,
          optionsValues,
        });
      }

      if (this.chosenAction === UPDATE_PRIMARY_AGENT) {
        params = merge(params, {
          optionsLabelText: 'New Primary Agent:',
          showOptions: true,
          optionsValues: this.agents.map(a => a.fullName),
          allowEmptyOption: true,
          requireOption: false,
          showAttachmentButton: this.canView('/conversations/inputbox/upload'),
        });
      }

      if (this.chosenAction === SEND_MESSAGE) {
        const channels = this.allResultsSelected
          ? this.sortedChannels.filter(channel => this.showSmsChannel || channel !== CHANNELS.SMS)
          : this.getAvailableChannels(this.selectedCustomers);

        params = merge(params, {
          optionsLabelText: 'Channel:',
          showOptions: true,
          optionsValues: channels,
          allowEmptyOption: false,
          requireOption: true,
          requireMessage: true,
          showAttachmentButton: this.canView('/conversations/inputbox/upload'),
        });
      }

      if (this.chosenAction === UPDATE_CUSTOMER_SEGMENTS) {
        const optionsValues = ['attach', 'detach'];
        const multiOptionsValues = difference(this.segments, NON_DELETABLE_SEGMENTS);
        params = merge(params, {
          optionsLabelText: 'Mode:',
          showOptions: true,
          multiOptionsLabelText: 'Segments:',
          showMultiOptions: true,
          requireMultiOptions: true,
          optionsValues,
          multiOptionsValues,
          showMessage: false,
          requireOption: true,
        });
      }

      if (this.chosenAction === UPDATE_ASSIGNED_AGENTS) {
        const optionsValues = ['attach', 'detach'];
        const multiOptionsValues = this.agents.map(a => a.fullName);
        params = merge(params, {
          optionsLabelText: 'Mode:',
          showOptions: true,
          multiOptionsLabelText: 'Agents:',
          showMultiOptions: true,
          requireMultiOptions: true,
          optionsValues,
          multiOptionsValues,
          showMessage: false,
          requireOption: true,
        });
      }

      if (this.chosenAction === CLOSE_CONVERSATION) {
        params = merge(params, {
          showMessage: false,
        });
      }

      return params;
    },
    helpText() { return this.$t('customer_tab.actions.description_tooltip') },
    actionsErrorText() {
      const stringPath = 'customer_tab.actions.error_tooltip.';
      return {
        title: this.$t(`${stringPath}title`),
        description: this.$t(`${stringPath}description`),
      };
    },
    hasRowSelections() {
      return this.selectedRowsCount > 0;
    },
    visibleCustomerTableColumns() {
      return Object.entries(this.columnsConfig)
        .filter(([, columnConfig]) => get(columnConfig, 'show', true))
        .map(([columnName, columnConfig]) => ({
          name: columnName,
          order: get(columnConfig, 'order', Number.MAX_SAFE_INTEGER),
        }))
        .sort((left, right) => left.order - right.order);
    },
    currentPageSelected() {
      return this.customers.length > 0 && this.customers.every(customer => this.selectedCustomers[customer.id]);
    },
    selectionScope() {
      return this.allResultsSelected
        ? `All ${this.selectedRowsCount.toLocaleString()} results selected`
        : `${this.selectedRowsCount.toLocaleString()} selected`;
    },
    selectedRowsCount() {
      return this.allResultsSelected ? this.pagination.rowCount : Object.keys(this.selectedCustomers).length;
    },
    advancedCustomerAgentOptions() {
      return {
        'Messages sent': 'messages_sent',
        'No messages sent': 'no_messages_sent',
        'Phone number provided': 'has_phone_number',
        'Phone number not provided': 'has_no_phone_number',
        ...(this.canView('/customers/visibility/no_agent_assigned') ? { Unassigned: 'unassigned' } : {}),
        ...(this.canView('/customers/visibility/no_primary_agent_assigned') ? { 'Primary selected': 'primary_selected', 'Primary not selected': 'primary_not_selected' } : {}),
        ...(this.canView('/customers/visibility/in_queue') ? { Queued: 'queued', 'Not queued': 'not_queued' } : {}),
        'Feedback sent': 'feedback_sent',
        'Feedback completed': 'feedback_returned',
        Unresponded: 'unresponded',
        'Message typed': 'typed',
        'Message not typed': 'not_typed',
      };
    },
  },
  methods: {
    ...mapActions({
      getCustomers: 'customersDashboard/getCustomers',
      exportCustomersAction: 'customersDashboard/exportCustomers',
      updateCustomerSegmentsAction: 'customersDashboard/updateCustomerSegments',
      updateCustomerAssignedAgentsAction: 'customersDashboard/updateCustomerAssigedAgents',
      updatePrimaryAgentFromSelectedCustomers: 'customersDashboard/updatePrimaryAgentFromSelectedCustomers',
      removePrimaryAgentFromSelectedCustomers: 'customersDashboard/removePrimaryAgentFromSelectedCustomers',
      selectCustomers: 'customersDashboard/selectCustomers',
      deselectAllCustomers: 'customersDashboard/deselectAllCustomers',
      sendMessageToSelectedCustomers: 'customersDashboard/sendMessageToSelectedCustomers',
      sendSurveyToSelectedCustomers: 'customersDashboard/sendSurveyToSelectedCustomers',
      closeConversationToSelectedCustomers: 'customersDashboard/closeConversationToSelectedCustomers',
    }),

    getCustomerName(customer) {
      return customer.fullName;
    },

    getCustomerEmail(customer) {
      return get(customer, 'profile.email', '');
    },

    getCustomerPhone(customer) {
      return get(customer, 'profile.phone', '');
    },

    getCustomerExternalId(customer) {
      return get(customer, 'profile.external_id', '');
    },

    getCustomerTableColumnLabel(columnName) {
      return this.$t(`customer_tab.table.columns.${columnName}`);
    },

    getIsCustomerTableColumnVisible(columnName) {
      return get(this.columnsConfig, `${columnName}.show`, true);
    },

    getFormattedCustomerSegment(customer) {
      return formatCustomerSegmentsForDisplay(get(customer, 'customer_segments', []));
    },
    getFormattedJoinedDate(customer) {
      return dayjs(customer.created_at).format('MM/DD/YYYY');
    },


    getFormattedLastChatTime(customer) {
      const lastChatTime = get(customer, 'last_chat_details.last_chat_time');
      if (!lastChatTime) {
        return 'Lynq';
      }
      return dayjs(lastChatTime).format('MM/DD/YYYY hh:mm A');
    },
    getFormattedFeedbackTime(customer, path) {
      const time = get(customer, path);
      if (!time) {
        return '';
      }
      return dayjs(time).format('MM/DD/YYYY hh:mm A');
    },
    goToLastConversation(customer) {
      const lastConversationCustomerId = customer.id;
      if (!lastConversationCustomerId) {
        return;
      }
      this.$router.push({
        name: 'ConversationByCustomerId',
        params: { id: lastConversationCustomerId },
      });
    },

    getPrimaryAgentName(customer) {
      return upperFirst(get(customer, 'primary_agent_details.full_name', ''));
    },

    getAssignedAgentName(agent) {
      return upperFirst(get(agent, 'full_name', ''));
    },
    getTruncatedAssignedAgentName(agent) {
      return limitedString(this.getAssignedAgentName(agent), this.maxAssignedAgentNameLength);
    },
    filterToAgent(agent) {
      const primaryAgentName = get(agent, 'full_name', '');
      if (primaryAgentName) {
        this.$router.push({
          name: 'Users',
          query: {
            query: primaryAgentName,
          },
        });
      }
    },

    closeActionPanel() {
      this.chosenAction = null;
      this.actionFormShown = false;
    },

    onSortName(a, b) {
      return get(a, 'fullName', '').toLowerCase().replace(/\s+/g, '') <= get(b, 'fullName', '').toLowerCase().replace(/\s+/g, '') ? 1 : -1;
    },
    onSortEmail(a, b) {
      return this.getCustomerEmail(a).toLowerCase() <= this.getCustomerEmail(b).toLowerCase() ? 1 : -1;
    },

    onSortPhone(a, b) {
      return this.getCustomerPhone(a).toLowerCase() <= this.getCustomerPhone(b).toLowerCase() ? 1 : -1;
    },

    onSortExternalId(a, b) {
      return this.getCustomerExternalId(a).toLowerCase() <= this.getCustomerExternalId(b).toLowerCase() ? 1 : -1;
    },

    onSortJoinedDate(a, b) {
      return get(a, 'created_at', '') <= get(b, 'created_at', '') ? 1 : -1;
    },

    onSortByTime(path) {
      return (a, b) => {
        const leftTime = get(a, path);
        const rightTime = get(b, path);

        //on downword sort, undefined values should be top
        //on upward sort, undefined should be bottom
        if (!leftTime) {
          return 1;
        }

        if (!rightTime) {
          return -1;
        }

        return leftTime <= rightTime ? 1 : -1;
      };
    },

    onSortPrimaryAgent(a, b) {
      return this.getPrimaryAgentName(a) <= this.getPrimaryAgentName(b) ? 1 : -1;
    },

    onSortCustomerSegment(a, b) {
      return get(a, 'profile.customer_segment', '') <= get(b, 'profile.customer_segment', '') ? 1 : -1;
    },

    isCustomerSelectable() {
      return !this.bulkAsyncActions || (!this.allResultsSelected && !this.isLoading);
    },

    async selectAllResults() {
      if (!this.bulkAsyncActions || !this.currentPageSelected || this.isLoading) return;
      this.selectionQuery = cloneDeep(omit(this.params, ['offset', 'limit']));
      this.allResultsSelected = true;
    },

    onSelect(selection = []) {
      if (this.bulkAsyncActions && (this.allResultsSelected || this.isLoading)) return;
      if (selection.length > 0) {
        this.selectCustomers(selection);
      } else {
        this.deselectAllCustomers();
      }
    },

    clearCustomerTableSelection() {
      this.$refs.customersTable?.clearSelection?.();
    },

    clearCustomerSelections() {
      this.allResultsSelected = false;
      this.selectionQuery = null;
      this.closeActionPanel();
      this.deselectAllCustomers();
      this.clearCustomerTableSelection();
    },

    getAvailableChannels(customers) {

      const customersList = isEmpty(customers) ? [] : Object.values(customers);

      let availableChannels = [
        ...new Set(
          customersList.flatMap(customer =>
            isArray(customer.conversation_channels) ? customer.conversation_channels : [],
          ),
        ),
      ].sort((a, b) => String(a).localeCompare(String(b)));

      if (!this.showSmsChannel) {
        availableChannels = availableChannels.filter(channel => channel !== CHANNELS.SMS);
      }

      return availableChannels;
    },

    updateCustomerSegments({ customers, selectedOption, selectedMultiOption, selection }) {
      return this.updateCustomerSegmentsAction({
        mode: selectedOption,
        segments: selectedMultiOption,
        customers,
        selection,
      });
    },

    async updateAssignedAgents({ customers, selectedOption, selectedMultiOption, selection }) {
      const agents = this.agents
        .filter(a => selectedMultiOption.includes(a.fullName))
        .map(a => a.id);
      return this.updateCustomerAssignedAgentsAction({
        mode: selectedOption,
        agents,
        customers,
        selection,
      });
    },

    sendMessageToCustomers({ customers, message, selectedOption, files, expirationMessageDate, selection }) {
      return this.sendMessageToSelectedCustomers({
        customers,
        selection,
        message,
        files,
        channel: selectedOption,
        expirationMessageDate: expirationMessageDate ?
          dayjs(expirationMessageDate).utc().format('YYYY-MM-DD HH:mm:ss.SSSZ') :
          expirationMessageDate,
      });
    },

    sendSurveyToCustomers({ customers, message, selectedOption, selection }) {
      return this.sendSurveyToSelectedCustomers({
        customers,
        selection,
        message,
        surveyType: selectedOption,
      });
    },

    closeConversationToCustomers({ customers, selection }) {
      return this.closeConversationToSelectedCustomers({ customers, selection });
    },

    chooseAction(action) {
      if (this.actionOptions[action]) {
        this.chosenAction = action;
        this.actionFormShown = true;
      }
    },

    async commitAction({
      actionValue, items, message, selectedOption, selectedMultiOption, files, expirationMessageDate,
    }) {
      if (this.bulkAsyncActions) {
        return this.commitAsyncAction({ actionValue, message, selectedOption, selectedMultiOption, files, expirationMessageDate });
      }
      const allSelectedCustomers = Object.values(items);

      // Exclude customers that cannot receive the message with the selected channel.
      let customersToCommit = allSelectedCustomers;
      const customersWithoutSelectedChannel = actionValue === SEND_MESSAGE
        ? this.getCustomerWithoutSelectedChannel(allSelectedCustomers, selectedOption)
        : [];

      if (customersWithoutSelectedChannel.length) {
        const list = `<ul>${customersWithoutSelectedChannel.map(item => `<li>${item.id}</li>`).join('')}</ul>`;
        const optionVal = selectedOption === CHANNELS.SMS ? 'SMS' : selectedOption;
        try {
          await this.$aiq.confirm(
            this.$t('customer_dashboard.sending_message.error_title'),
            `${this.$t('customer_dashboard.sending_message.error_dialog', {selectedOption: optionVal})}
            <br><br>
            ${list}`,
            { showCancelButton: false},
          );
        }
        catch {
          // Nothing to handle, just continue since there is no cancel button for this dialog
        }

        customersToCommit = allSelectedCustomers.filter(c => (
          (c.conversation_channels || []).includes(selectedOption)
        ));
      }

      if (customersToCommit.length === 0) {
        this.chosenAction = null;
        this.actionInProgress = false;
        this.clearCustomerSelections();
        await this.loadCustomers(this.params);
        return;
      }

      try {
        this.actionInProgress = true;
        const res = await this.actionOptions[actionValue].commitActionFunction({
          customers: customersToCommit,
          message,
          files,
          selectedOption,
          selectedMultiOption,
          expirationMessageDate,
        });

        if (get(res, 'id')) {
        // the action will be running asynchronously.
        // and the given id is a job id
          this.$aiq.notify.info('The request accepted. You will be notified once the job is completed.');
        } else if (get(res, 'result')) {
        // The number of customers is small and the request is
        // completed
          this.$aiq.notify.success('Successful');
        } else {
          this.$aiq.notify.error('Unexpected response');
        }
      } catch (err) {
        this.$aiq.error.error('Failed');
      }
      finally {
        this.chosenAction = null;
        this.actionInProgress = false;
        this.clearCustomerSelections();
        await this.loadCustomers(this.params);
      }
    },

    async commitAsyncAction(options) {
      const action = this.actionOptions[options.actionValue].commitActionFunction;
      const scope = this.selectionScope;
      const query = this.allResultsSelected ? cloneDeep(this.selectionQuery) : null;
      const selection = query ? { mode: 'all', filters: query } : { mode: 'explicit' };
      let customers = cloneDeep(Object.values(this.selectedCustomers));
      this.clearCustomerSelections();
      this.$aiq.notify.info(`${options.actionValue}: processing (${scope}). You can continue working.`);
      try {
        const skipped = !query && options.actionValue === SEND_MESSAGE
          ? this.getCustomerWithoutSelectedChannel(customers, options.selectedOption).length : 0;
        if (skipped) customers = customers.filter(c => (c.conversation_channels || []).includes(options.selectedOption));
        if (!query && !customers.length) throw new Error('No eligible customers');
        const response = await action({ ...options, customers, selection });
        if (get(response, 'id')) {
          this.$aiq.notify.info(`${options.actionValue}: request accepted (${scope}). You will be notified when the job completes.`);
        } else if (get(response, 'result')) {
          const result = response.result;
          const updated = Array.isArray(result.updated) ? result.updated.length : 0;
          const backendSkipped = Array.isArray(result.skiped) ? result.skiped.length : 0;
          const failed = Array.isArray(result.failed) ? result.failed.length : 0;
          const details = `${updated} updated, ${backendSkipped + skipped} skipped, ${failed} failed`;
          const notify = failed ? this.$aiq.notify.error : this.$aiq.notify.success;
          notify(`${options.actionValue} completed (${scope}): ${details}.`);
        } else {
          throw new Error('Unexpected response');
        }
      } catch (error) {
        this.$aiq.notify.error(`${options.actionValue} failed (${scope}): ${error.message}`);
      }
    },

    getCustomerWithoutSelectedChannel(customers, channel) {
      return customers.filter(c => !(
        (c.conversation_channels || []).includes(channel)
      ));
    },

    async loadList(params, options = {}) {
      const {
        searchTerm,
        searchOffset,
        customerAgentOption,
        customerChannelOption,
        customerSegmentOption,
        customerTagOption,
        customerTeamOption,
        agentFilterOption,
        primaryAgentFilterOption,
        customerProfileOption,
        periods,
      } = params;


      if (SPECIAl_CHAR.test(searchTerm)) {
        this.$aiq.notify.error(this.$t('metrics_tab.search.notify_errors.no_special_characters'));
        this.$refs.managementPage.updateSearchTerm(searchTerm.replace(SPECIAl_CHAR, ''));
        return;
      }

      this.isLoading = true;
      this.actionInProgress = true;

      const previousQuery = omit(this.params, ['offset', 'limit']);
      this.params = {
        limit: this.searchPageSize,
        query: searchTerm,
        offset: searchOffset,
      };

      this.$refs.customersTable.sort(this.params.date_type, 'ascending');

      if (this.advancedDashboardAgentFilter) {
        this.params.filter_agent_id = this.advancedDashboardAgentFilter.agentId;
        this.params.filter_type = this.advancedDashboardAgentFilter.filterType;
      }

      if (Array.isArray(customerAgentOption) && customerAgentOption.length) {
        this.params.attributes = customerAgentOption.map(option => this.advancedCustomerAgentOptions[option]).filter(Boolean);
      }

      if (customerChannelOption) {
        this.params.channel = customerChannelOption;
      }

      if (customerSegmentOption) {
        this.params.segments = customerSegmentOption;
      }

      if (customerProfileOption) {
        this.params.profile_field = customerProfileOption.field;
      }

      if (Array.isArray(customerTagOption) && customerTagOption.length) {
        this.params.tags = customerTagOption;
      }

      if (Array.isArray(customerTeamOption) && customerTeamOption.length) {
        this.params.team_ids = customerTeamOption;
      }

      if (Array.isArray(agentFilterOption) && agentFilterOption.length) {
        this.params.agent_ids = agentFilterOption;
      }

      if (Array.isArray(primaryAgentFilterOption) && primaryAgentFilterOption.length) {
        this.params.primary_agent_ids = primaryAgentFilterOption;
      }

      if (Array.isArray(periods) && periods.length) {
        const formattedPeriods = periods
          .map(period => ({
            date_type: period.date_type,
            ...(period.period_start && { period_start: dayjs(period.period_start).unix() }),
            ...(period.period_end && { period_end: dayjs(period.period_end).unix() }),
          }))
          .filter(period => period.date_type && (period.period_start || period.period_end));

        if (formattedPeriods.length) {
          this.params.periods = JSON.stringify(formattedPeriods);
        }
      }

      if (this.watchableTeamIds.length) {
        this.params.teams = this.watchableTeamIds;
      }
      if (this.bulkAsyncActions && !isEqual(previousQuery, omit(this.params, ['offset', 'limit']))) {
        this.clearCustomerSelections();
      }
      return this.loadCustomers(this.params, true, this.bulkAsyncActions || options.replaceCustomers);
    },

    applyAdvancedFilter(filters) {
      if (this.bulkAsyncActions) this.clearCustomerSelections();
      else this.clearCustomerTableSelection();
      this.advancedDashboardAgentFilter = filters.dashboardAgentFilter || null;
      const managementPage = this.$refs.managementPage;

      if (managementPage) {
        managementPage.applyFetchState(filters);
        managementPage.fetchData();
        return;
      }

      this.loadList(filters);
    },

    fetchPageAtOffset(offset) {
      const managementPage = this.$refs.managementPage;

      if (!managementPage) {
        return;
      }

      managementPage.offset = offset;
      managementPage.fetchData({ replaceCustomers: true });
    },

    fetchFirstPageAfterPageSizeChange() {
      this.$nextTick(() => {
        this.fetchPageAtOffset(0);
      });
    },

    async loadSome(customersCount) {
      this.isLoadingAll = true;
      let hasMoreVisibleResults = true;

      while (hasMoreVisibleResults && this.isLoadingAll && !this.isAllCustomersPageLoaded() && customersCount > this.searchPageSize) {
        const previousVisibleResults = this.getVisibleCustomerResults();
        this.params.offset = this.pagination.offset + this.searchPageSize;
        await this.loadCustomers(this.params); // eslint-disable-line no-await-in-loop
        customersCount -= this.searchPageSize;
        hasMoreVisibleResults = this.getVisibleCustomerResults() > previousVisibleResults;
      }
      this.syncManagementPageOffset();
      this.stopLoadAll();
    },

    async stopLoadAll() {
      this.isLoadingAll = false;
      this.isLoading = false;
    },

    getVisibleCustomerResults() {
      return Math.min(
        this.pagination.offset + this.pagination.limit,
        this.pagination.rowCount,
      );
    },

    syncManagementPageOffset() {
      if (this.$refs.managementPage) {
        this.$refs.managementPage.offset = this.getVisibleCustomerResults();
      }
    },

    isAllCustomersPageLoaded() {
      return this.pagination.rowCount <= this.getVisibleCustomerResults();
    },

    toggleOpen(isOpen) {
      this.isFilterOpen = isOpen;
    },

    async exportCustomers() {
      if (!this.showCustomersExport || this.isExporting || this.isLoading) {
        return;
      }

      this.isExporting = true;
      try {
        if (this.pagination.rowCount > CUSTOMERS_EXPORT_WARNING_THRESHOLD) {
          try {
            await this.$aiq.confirm(
              this.$t('customer_dashboard.export_warning.title'),
              this.$t('customer_dashboard.export_warning.message'),
              {
                cancelButtonText: this.$t('customer_dashboard.export_warning.cancel'),
                confirmButtonText: this.$t('customer_dashboard.export_warning.confirm'),
              },
            );
          } catch (_) {
            return;
          }
        }

        const response = await this.exportCustomersAction({
          ...this.params,
          time_zone: dayjs.tz.guess(),
        });
        downloadDataAsFile(response, 'customers', '.csv');
        this.$aiq.notify.success(this.$t('customer_dashboard.export_started'));
      } catch (err) {
        this.$aiq.notify.error(this.$t('customer_dashboard.export_failed'));
      } finally {
        this.isExporting = false;
      }
    },

    downloadData() {
      downloadAsFile({
        dataType: 'table',
        data: this.customers.map(customer => ({
          fullname: this.getCustomerName(customer),
          email: this.getCustomerEmail(customer),
          joined_date: this.getFormattedJoinedDate(customer),
          last_chat_time: this.getFormattedLastChatTime(customer),
          customer_segment: this.getFormattedCustomerSegment(customer),
          primary_agent: this.getPrimaryAgentName(customer),
        })),
      });
    },

    async loadCustomers(params, refresh = true, replaceCustomers = false) {
      this.isLoading = true;
      try {
        const preservedSelection = this.bulkAsyncActions ? { ...this.selectedCustomers } : null;
        const { customers, pagination } = await this.getCustomers({
          params,
          update: replaceCustomers || (refresh && (get(params, 'offset') === 0)),
        });
        this.pagination = pagination;
        if (preservedSelection) {
          await this.selectCustomers(Object.values(preservedSelection));
          await this.$nextTick();
          this.customers.forEach(customer => {
            this.$refs.customersTable?.toggleRowSelection(customer, this.allResultsSelected || !!preservedSelection[customer.id]);
          });
        }
        return customers;
      } catch (err) {
        this.$aiq.notify.error(this.$t('customer_dashboard.loading_failed'));

        this.pagination = PAGINATION_DEFAULT;
        return [];

      } finally {
        this.actionInProgress = false;
        this.isLoading = false;
      }
    },

    // Deprecated
    async removePrimaryAgentFromCustomers({ customers, message }) {
      await this.removePrimaryAgentFromSelectedCustomers({
        customers,
        message,
      });
      this.deselectAllCustomers();
    },

    updatePrimaryAgentFromCustomers({ customers, message, selectedOption, files, selection }) {
      if (!selectedOption) {
        return this.updatePrimaryAgentFromSelectedCustomers({
          mode: 'remove',
          selection,
          customers,
          message,
          files,
        });
      }

      const agent = this.agents.find(a => a.fullName === selectedOption);
      return this.updatePrimaryAgentFromSelectedCustomers({
        mode: 'replace',
        selection,
        customers,
        message,
        files,
        agent,
      });
    },

    async selectVisibleRows() {
      await this.$nextTick();
      this.$refs.customersTable.clearSelection();
      this.customers.forEach(customer => {
        this.$refs.customersTable.toggleRowSelection(customer, true);
      });
    },
  },
  beforeUnmount() {
    this.stopLoadAll();
  },
};

</script>

<style lang="scss" scoped>
@import "../../styles/aiq-mixins.scss";
@import "../../styles/media-queries.scss";

  .bulk-actions-layout {
    :deep(.header_actions) {
      align-items: flex-end;
    }

    :deep(.bulk-selection-toolbar) {
      flex: 0 1 auto;
      justify-content: flex-start;
    }

    :deep(.right-controls .totals) {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      min-height: 30px;
    }
  }

  .el-table--fit{
    height: calc(100% - 140px);
  }

  .status-indicator {
    &::before {
      margin-top: 1px;
    }
  }

  .agent-button {
    white-space: break-spaces;
    text-align: left;
    word-break: break-word;
    margin-left: 3px;
    padding-top: 3px;
    padding-bottom: 3px;
  }

  :deep(.aiq-table-totals) {
    .el-tag {
      .el-tag__close {
        color:#409eff;

        &:hover {
          color:white;
        }
      }
    }
  }

</style>

<style lang="scss">
@import "../../styles/aiq-mixins.scss";
@import "../../styles/media-queries.scss";

.customers-scrollbar {

  .el-table__header {
    .cell {
      word-break: normal;
    }
  }

  .el-table__body-wrapper {
    @include scrollable(calc(100vh - 247px));

    .date-button span{
      white-space: normal;
      word-break: break-all;
    }
  }
}
.add-edit-actions-panel {
  .el-dialog {
      @include mobile {
        min-width: 90%;
    }
  }
}

.date-range-filter.filter-in-header {
  .date-range-filter-date-field.filter-in-header {
    .el-date-editor {
      .el-input__wrapper {
        height: 30px !important;
      }
    }
  }
}

</style>
